/**
 * RWBR — THE BOOTH: what actually happened, mechanically, in words.
 *
 * D-EZ-74. Howard asked for commentary that is *"more informative"*, and the
 * missing ingredient turned out to be the one thing the booth had never been
 * told: WHY.
 *
 * The payload sent to the writer was facts and setting. Facts are outcomes —
 * "9 cars caught up in it", "he's led 8 turns today". Setting is a place. What
 * neither carries is the MECHANISM: a duel was called and lost, a car escaped
 * trouble and one did not, a crew won the stop, the camera was on the back of
 * the field. So the analyst's seat — the whole point of which is the question
 * after "what happened" — had nothing to answer with except the outcome the
 * play-by-play had just described. Two people saying the same thing in
 * different words, which is what "add another voice" degenerates into when the
 * second voice knows nothing the first one did not.
 *
 * ⚠ THE MECHANISM IS NOT THE ARITHMETIC, AND THIS FILE IS THE LINE BETWEEN
 * THEM. `RuleEvent` carries `dice`, `modifiers` and `ruleCitation`, and it is
 * tempting to forward all three and let a good model sort it out. It must not
 * happen, for a reason Howard already ruled on at D-EZ-69 when he asked to be
 * told what happened *"without adding chart references etc."*:
 *
 *     "Otis Barkley sends it and gets it wrong — red 3 against 2 gas."
 *     "The DUEL table points at #6 cars in MIDDLE group."
 *
 * Both true. Both a chart talking, not a person. A spectator at a racetrack
 * knows a man tried a move and it did not come off; he does not know, and must
 * not be told, which die decided it.
 *
 * So this translates `momentTag` — the engine's own stable name for the KIND of
 * thing that happened — into one plain sentence about racing. No numbers, no
 * citation, no die, no chart. The model gets to know that a duel was declined
 * rather than lost, and has to say so in its own words.
 *
 * D-003 holds: nothing here is a claim about a specific car. It is a
 * description of the category of event the ledger already recorded.
 */

import type { RuleEvent } from '../ruleEvent.ts';
import type { TurnResult } from '../turn.ts';

/**
 * `momentTag` → one sentence of mechanism, in racing English.
 *
 * Keyed on the full tag where the distinction matters and on the prefix where
 * it does not. A tag with no entry produces NOTHING rather than a guess — the
 * booth simply is not told why, which is exactly how it worked before this
 * file existed and is therefore always safe.
 */
const MECHANISM: Record<string, string> = {
  // --- passing and defending ---
  'challenge.sequence': 'a car came up on the one in front and had a go at him',
  'challenge.no-defender': 'there was nobody in front of him to fight',
  'challenge.void-triangle': 'the move was called off before it started',
  'challenge.void-triples': 'the move was called off before it started',

  // --- duels ---
  'duel.called': 'an opportunity to duel came up',
  'duel.win': 'he forced the issue and it came off',
  'duel.declined': 'the challenge was there and he chose not to take it',
  'duel.void-triangle': 'the fight was waved off before it started',

  // --- trouble and wrecks ---
  'trouble.escaped': 'he got out of the way of something that could have ended his day',
  'trouble.collected': 'he was collected in the incident',
  'trouble.caution': 'it was bad enough to bring out the yellow',
  'trouble.chip-saved': 'he spent something he had been saving to get out of it',
  'wreck.big-one': 'the incident spread into another group of cars',
  'feud.wreck': 'this was not an accident — these two have been at it',

  // --- repairs ---
  'repair.repaired': 'they patched it up and sent him back out',
  'repair.impossible': 'the crew could not return the car to the race',

  // --- pit road ---
  'pit.fast-pit': 'the crew got him out quicker than the men around him',
  'pit.lead-change': 'the lead changed hands in the pits rather than on the racetrack',
  'pit.lead-held': 'the leader kept the lead through the stops',
  'pit.chip-reorder': 'the cars carrying an advantage were promoted in the order leaving the pits',
  'pit.box-swap': 'they shuffled the order coming off pit road',
  'pit.no-challenger': 'nobody was close enough to take the lead off him in the pits',

  // --- the yellow and the restart ---
  'turn.yellow': 'the field is behind the pace car and the gaps are gone',
  'yellow.lucky-dog': 'the first car a lap down gets it back under the caution',
  'restart.turn-cancelled': 'the restart incident ended the turn before its regular action',

  // --- chips, the resource nobody watches ---
  'chip.laps-led': 'leading is worth something he can spend later',
  'chip.reserve-activated': 'he has reached into the reserve',
  'chip.bg-lost': 'the move to the back cost him an advantage he had been holding',

  // --- injuries ---
  'injury.minor': 'a minor injury was recorded',
  'injury.moderate': 'he is hurt and it is going to cost him time',
  'injury.serious': 'he is hurt badly and his day is over',

  // --- the field itself ---
  'turn.final-black-five': 'the final action focuses on the cars nearer the front',
  'churn.yellow-deferred': 'the caution from the action at the back takes effect after the regular action',
};

/** Prefix fallbacks, for tags whose family says enough on its own. */
const FAMILY: Record<string, string> = {
  duel: 'somebody forced the issue instead of waiting for a run',
  trouble: 'an incident was recorded',
  pit: 'this was decided on pit road',
  feud: 'there is something between these two and it came out on the racetrack',
  challenge: 'a car had a go at the one in front of him',
  chip: 'a driver’s available advantages changed',
  injury: 'somebody is hurt',
  repair: 'the crew went to work on a damaged car',
  wreck: 'a lot of cars found each other at once',
};

/**
 * WHERE THE CAMERA WAS. `RaceCamRead.group` is the part of the field the turn
 * resolved in, which is a legitimate thing for a broadcast to say and is not a
 * chart reference once it stops being called the RACE CAM.
 *
 * ⚠ `printed`, `black`, `metallic`, `total` and `doubles` are NOT forwarded and
 * must not be. Those are the roll.
 */
const WHERE: Record<string, string> = {
  top: 'at the front of the field',
  middle: 'in the middle of the pack',
  bottom: 'at the back of the field',
  duel: 'around a possible duel',
};

export interface Why {
  /** One sentence of mechanism, or null when the ledger's tag says nothing useful. */
  mechanism: string | null;
  /** Which part of the field this turn happened in, or null. */
  where: string | null;
  /** True when the turn was run under the yellow. */
  underYellow: boolean;
}

const mechanismFor = (tag: string): string | null => {
  if (MECHANISM[tag]) return MECHANISM[tag];
  const family = tag.split('.')[0];
  return FAMILY[family] ?? null;
};

/**
 * Read the mechanism of a turn, for the writer.
 *
 * `lead` is the event the booth chose to talk about, so the mechanism describes
 * THAT rather than whatever happened to be largest — the alternative is a beat
 * about a pass being explained by a pit stop that happened in the same ten
 * seconds.
 */
export function whyOf(t: TurnResult, lead: RuleEvent | null): Why {
  const tag = lead?.momentTag ?? '';
  return {
    mechanism: tag ? mechanismFor(tag) : null,
    where: t.raceCam ? (WHERE[t.raceCam.group] ?? null) : null,
    underYellow: t.kind === 'yellow',
  };
}

/**
 * Every word this block can put in front of the model, for the fact gate.
 *
 * The verifier refuses a capitalised word it has not seen (`verify.ts`), and a
 * mechanism sentence is the model's licence to use words like "pace car" and
 * "yellow". Handing the vocabulary over is the same principle `alsoAllow`
 * already uses for the setting: a word is sayable because it was given, not
 * because the gate got looser.
 */
export function whyWords(why: Why): string[] {
  const out: string[] = [];
  for (const s of [why.mechanism, why.where]) {
    if (!s) continue;
    for (const w of s.split(/[^A-Za-z'’-]+/)) if (w.length > 1) out.push(w);
  }
  return out;
}
